import { useState } from "react";
import { Headphones, FileQuestion, Users, PlayCircle, Briefcase, Handshake } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SectionHeader } from "@/components/SectionHeader";
import { InquiryForm } from "@/components/InquiryForm";
import { cn } from "@/lib/utils";

const CARDS = [
  { icon: Headphones, title: "Technical Support", body: "Engineering guidance for installations, configurations and integrations." },
  { icon: FileQuestion, title: "Product Enquiries", body: "Specifications, datasheets and compatibility consultations." },
  { icon: Users, title: "Dealer Support", body: "Ongoing support for our growing network of Fibaro partners." },
  { icon: PlayCircle, title: "Request a Demo", body: "Experience Fibaro live at our Coimbatore experience centre." },
  { icon: Briefcase, title: "Project Consultation", body: "Specification support for architects, designers and builders." },
  { icon: Handshake, title: "Become a Dealer", body: "Apply to join our nationwide Fibaro partner network." },
];

const FORMS = [
  {
    key: "technical",
    label: "Technical Support Request",
    type: "technical",
    fields: ["name", "company", "email", "phone", "subject", "message"],
    cta: "Submit Request",
  },
  {
    key: "consultation",
    label: "Project Consultation Request",
    type: "consultation",
    fields: ["name", "company", "email", "phone", "city", "message"],
    cta: "Request Consultation",
  },
  {
    key: "dealer",
    label: "Dealer Enquiry",
    type: "dealer",
    fields: ["name", "company", "city", "state", "phone", "email", "current_brands", "message"],
    cta: "Apply to Partner",
  },
];

export default function Support() {
  const [activeForm, setActiveForm] = useState("technical");
  const current = FORMS.find((f) => f.key === activeForm);

  return (
    <>
      <section className="pt-40 pb-20" data-testid="support-hero">
        <div className="container-edge max-w-4xl">
          <p className="eyebrow flex items-center gap-3"><span className="divider-gold" /> Support</p>
          <h1 className="display-1 mt-6">
            How can we <span className="italic text-gold">help</span> you?
          </h1>
          <p className="mt-8 text-base md:text-lg text-muted-foreground leading-relaxed">
            Whether you need engineering support, specification guidance or wish to experience Fibaro in
            person — our team is ready to assist.
          </p>
        </div>
      </section>

      <section className="py-16" data-testid="support-cards">
        <div className="container-edge">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
            {CARDS.map((c, i) => (
              <Reveal key={c.title} delay={(i % 3) * 80} className="bg-background p-8 group hover:bg-secondary/50 transition-colors">
                <c.icon className="h-7 w-7 text-gold" />
                <h3 className="font-display text-xl mt-6">{c.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{c.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-secondary/40" data-testid="support-forms">
        <div className="container-edge grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-4">
            <p className="eyebrow flex items-center gap-3"><span className="divider-gold" /> Send a Request</p>
            <h2 className="display-2 mt-5">
              Choose your <span className="italic text-gold">enquiry</span> type.
            </h2>
            <div className="mt-10 flex flex-col gap-1">
              {FORMS.map((f) => (
                <button
                  key={f.key}
                  data-testid={`form-tab-${f.key}`}
                  onClick={() => setActiveForm(f.key)}
                  className={cn(
                    "text-left py-4 border-b border-border text-sm uppercase tracking-[0.22em] transition-colors",
                    activeForm === f.key ? "text-gold border-gold" : "hover:text-gold"
                  )}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="lg:col-span-8">
            <Reveal key={current.key}>
              <div className="bg-background p-8 md:p-12 border border-border">
                <p className="eyebrow">{current.label}</p>
                <h3 className="font-display text-2xl md:text-3xl mt-3 mb-10">Tell us about your requirement.</h3>
                <InquiryForm
                  type={current.type}
                  testid={`form-${current.key}`}
                  sourcePage="support"
                  fields={current.fields}
                  submitLabel={current.cta}
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
